import { Card } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";
import { cn } from "@/shared/lib/utils";

type Shape = "answer" | "kpi" | "table" | "chart" | "list" | "markdown" | "generic";

interface SectionSkeletonProps {
  /** Hint that lets the skeleton mimic the silhouette of the section about to land. */
  shape?: Shape;
  className?: string;
}

// Per-type minimum heights (B4 anti-jitter rule 4). Reserving a floor close to
// the real section's rendered height stops the page from snapping when the
// skeleton is swapped for content — the dominant CLS source during streaming.
// Min (not fixed) height: content taller than the floor grows downward, which
// reads as natural and does not shift earlier sections.
const SHAPE_MIN_H: Record<Shape, string> = {
  answer: "min-h-[112px]",
  kpi: "min-h-[96px]",
  table: "min-h-[260px]",
  chart: "min-h-[324px]",
  list: "min-h-[200px]",
  markdown: "min-h-[140px]",
  generic: "min-h-[140px]",
};

/**
 * Section-shaped skeleton — reserves layout height so the next real section
 * morphs in without jolting the page. Shape is a hint; if absent we render a
 * neutral block. Generic shape is also the "more is coming" trailer.
 */
export function SectionSkeleton({ shape = "generic", className }: SectionSkeletonProps) {
  const minH = SHAPE_MIN_H[shape];

  if (shape === "answer") {
    return (
      <div className={cn("space-y-3 pb-2", minH, className)}>
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-11/12" />
        <Skeleton className="h-4 w-9/12" />
      </div>
    );
  }
  if (shape === "kpi") {
    return (
      <Card className={cn("overflow-hidden", minH, className)}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-card px-5 py-4 space-y-2">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-8 w-20" />
            </div>
          ))}
        </div>
      </Card>
    );
  }
  if (shape === "chart") {
    return (
      <Card className={cn("overflow-hidden", minH, className)}>
        <div className="px-5 py-3.5 border-b border-border">
          <Skeleton className="h-4 w-1/3" />
        </div>
        <div className="px-5 py-4">
          <Skeleton className="h-[260px] w-full" />
        </div>
      </Card>
    );
  }
  if (shape === "table") {
    return (
      <Card className={cn("overflow-hidden", minH, className)}>
        <div className="px-5 py-3.5 border-b border-border">
          <Skeleton className="h-4 w-1/3" />
        </div>
        <div className="divide-y divide-border">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="grid grid-cols-4 gap-3 px-5 py-3">
              {Array.from({ length: 4 }).map((__, j) => (
                <Skeleton key={j} className="h-3 w-full" />
              ))}
            </div>
          ))}
        </div>
      </Card>
    );
  }
  if (shape === "list") {
    return (
      <Card className={cn("overflow-hidden", minH, className)}>
        <div className="px-5 py-3.5 border-b border-border">
          <Skeleton className="h-4 w-1/3" />
        </div>
        <div className="divide-y divide-border">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="px-5 py-3 space-y-2">
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          ))}
        </div>
      </Card>
    );
  }
  if (shape === "markdown") {
    return (
      <Card className={cn("overflow-hidden", minH, className)}>
        <div className="px-5 py-3.5 border-b border-border">
          <Skeleton className="h-4 w-1/3" />
        </div>
        <div className="px-5 py-4 space-y-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-11/12" />
          <Skeleton className="h-3 w-9/12" />
        </div>
      </Card>
    );
  }
  return (
    <Card className={cn("p-5", minH, className)}>
      <div className="space-y-3">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-11/12" />
        <Skeleton className="h-3 w-9/12" />
      </div>
    </Card>
  );
}
